import React from "react";
import { Alert, Button, Tag, Typography } from "@arco-design/web-react";
import { IconLaunch, IconRefresh } from "@arco-design/web-react/icon";
import { Record } from "../Settings/SendingDomains/DnsSetupGuide";
import { setupChecks } from "./domainPool";

const { Text, Paragraph, Title } = Typography;

const WHY = {
  receive_mx: "Sends the domain's mail to Amazon, which passes it to the app.",
  amazon_verify: "Amazon only accepts mail for a domain it has verified. It looks for this record by itself.",
};

function CheckButton({ onCheck, checking }) {
  return (
    <Button type="primary" size="small" icon={<IconRefresh />} loading={checking} onClick={onCheck}>
      Check now
    </Button>
  );
}

function Warnings({ domain }) {
  return (domain.dns_warnings || []).map((w, i) => (
    <Alert key={w.code + i} type="warning" style={{ marginBottom: 8 }} title="Something to fix" content={w.message} />
  ));
}

// Checklist is the three things that must be true for seeds to get mail.
function Checklist({ domain }) {
  return (
    <div>
      {setupChecks(domain).map((c) => (
        <div key={c.key} style={{ display: "flex", gap: 8, alignItems: "baseline", marginTop: 4 }}>
          <Tag color={c.done ? "green" : "orange"} size="small">{c.done ? "Done" : "Not yet"}</Tag>
          <Text>{c.label}</Text>
        </div>
      ))}
    </div>
  );
}

// SpyDomainSetup walks a platform admin through getting a spy domain to
// receive mail. The app sets up Amazon itself; the admin adds two DNS records.
// A ready domain shows just the checklist.
export default function SpyDomainSetup({ domain, onCheck, checking }) {
  const guide = domain.guide || { name: "your DNS provider", steps: [], host_label: "Host", value_label: "Value", tips: [] };

  if (domain.ready_date) {
    return (
      <div style={{ maxWidth: 900 }}>
        <Warnings domain={domain} />
        <Title heading={6} style={{ marginTop: 4 }}>Receiving mail, and taking new seeds</Title>
        <Checklist domain={domain} />
        <div style={{ marginTop: 12 }}><CheckButton onCheck={onCheck} checking={checking} /></div>
      </div>
    );
  }

  let n = 1;
  return (
    <div style={{ maxWidth: 900 }}>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        This domain takes no seeds until all three checks pass, so no customer gets an address that can't receive mail.
      </Paragraph>
      <Checklist domain={domain} />
      <div style={{ height: 12 }} />
      <Warnings domain={domain} />

      <Title heading={6} style={{ marginTop: 12 }}>{n++}. Open the DNS settings at {guide.name}</Title>
      <ol style={{ marginTop: 4, paddingLeft: 20 }}>
        {(guide.steps || []).map((s) => <li key={s}>{s}</li>)}
      </ol>
      {guide.url && (
        <Button type="outline" size="small" icon={<IconLaunch />} href={guide.url} target="_blank" rel="noreferrer">
          Open {guide.name}
        </Button>
      )}

      {(domain.records || []).map((r) => (
        <div key={r.purpose}>
          <Title heading={6} style={{ marginTop: 20 }}>{n++}. Add the {r.type} record</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>{WHY[r.purpose]}</Paragraph>
          <Record record={r} guide={guide} />
        </div>
      ))}

      <Title heading={6} style={{ marginTop: 20 }}>{n++}. Check</Title>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        DNS changes can take from a few minutes to a few hours to show up. The app checks by itself every few
        minutes, and the domain starts taking new seeds as soon as everything is found.
      </Paragraph>
      <CheckButton onCheck={onCheck} checking={checking} />

      {(guide.tips || []).length > 0 && (
        <Alert
          type="info"
          style={{ marginTop: 20 }}
          title={`Tips for ${guide.name}`}
          content={<ul style={{ margin: 0, paddingLeft: 18 }}>{guide.tips.map((t) => <li key={t}>{t}</li>)}</ul>}
        />
      )}
    </div>
  );
}
